/* NTH motion layer — reveals, parallax, scroll drift, header, drag carousel.
   Pure JS, no dependencies. Call NTHMotion.init(root) after rendering new content. */
(() => {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  let parallaxEls = [], driftEls = [], ticking = false;

  /* ---------- reveals ---------- */
  const io = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  function initReveals(root) {
    $$('[data-reveal], [data-lines]', root).forEach(el => {
      if (reduce || !io) { el.classList.add('is-in'); return; }
      io.observe(el);
    });
    // stagger children of [data-stagger]
    $$('[data-stagger]', root).forEach(group => {
      [...group.children].forEach((c, i) => {
        if (!c.hasAttribute('data-reveal')) c.setAttribute('data-reveal', '');
        c.style.setProperty('--i', i % 4);
        if (reduce || !io) c.classList.add('is-in'); else io.observe(c);
      });
    });
  }

  /* ---------- parallax & drift (one rAF loop) ---------- */
  function measure() {
    parallaxEls = $$('[data-parallax]');
    driftEls = $$('[data-drift]');
  }
  function frame() {
    ticking = false;
    const vh = innerHeight;
    for (const el of parallaxEls) {
      const r = el.parentElement.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) continue;
      const speed = parseFloat(el.dataset.parallax) || 0.1;
      const offset = (r.top + r.height / 2 - vh / 2) * -speed;
      el.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    }
    for (const el of driftEls) {
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) continue;
      const p = 1 - (r.top + r.height) / (vh + r.height); // 0 → 1 while crossing viewport
      const amt = parseFloat(el.dataset.drift) || 1;
      el.style.transform = `translate3d(${(Math.min(1, Math.max(0, p)) * 10 * amt).toFixed(2)}vw, 0, 0)`;
    }
  }
  function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(frame); } }
  if (!reduce) { addEventListener('scroll', onScroll, { passive: true }); addEventListener('resize', () => { measure(); onScroll(); }); }

  /* ---------- header: transparent → solid, hide on scroll down ---------- */
  let lastY = scrollY;
  function headerTick() {
    const h = document.querySelector('[data-header]');
    if (!h) return;
    const y = scrollY;
    const overlay = h.hasAttribute('data-overlay');
    h.classList.toggle('is-solid', !overlay || y > 40 || document.body.classList.contains('lock'));
    if (!document.body.classList.contains('lock')) {
      const hide = y > 520 && y > lastY + 4;
      const show = y < lastY - 4 || y < 520;
      if (hide) h.classList.add('is-hidden');
      else if (show) h.classList.remove('is-hidden');
      document.body.classList.toggle('header-hidden', h.classList.contains('is-hidden'));
    }
    lastY = y;
  }
  addEventListener('scroll', headerTick, { passive: true });

  /* ---------- drag carousel with momentum ---------- */
  function initRails(root) {
    $$('[data-rail]', root).forEach(wrap => {
      const rail = wrap.querySelector('.rail');
      if (!rail || rail.dataset.bound) return;
      rail.dataset.bound = '1';
      const prev = wrap.querySelector('[data-rail-prev]'), next = wrap.querySelector('[data-rail-next]');
      const bar = wrap.querySelector('[data-rail-bar]');
      const step = () => { const c = rail.querySelector('.card'); return c ? c.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap || 0) : 300; };
      const update = () => {
        const max = rail.scrollWidth - rail.clientWidth;
        const x = Math.abs(rail.scrollLeft);
        if (bar) { const vis = rail.clientWidth / rail.scrollWidth; bar.style.transform = `translateX(${max ? (x / max) * (1 / vis - 1) * 100 : 0}%) scaleX(1)`; bar.style.width = (vis * 100) + '%'; }
        if (prev) prev.disabled = x < 4;
        if (next) next.disabled = x > max - 4;
      };
      prev && prev.addEventListener('click', () => rail.scrollBy({ left: -step() * (document.dir === 'rtl' ? -1 : 1), behavior: 'smooth' }));
      next && next.addEventListener('click', () => rail.scrollBy({ left: step() * (document.dir === 'rtl' ? -1 : 1), behavior: 'smooth' }));
      rail.addEventListener('scroll', update, { passive: true });
      addEventListener('resize', update);
      update();

      // mouse drag (touch uses native scrolling)
      let down = false, startX = 0, startL = 0, lastX = 0, lastT = 0, v = 0, moved = false, raf;
      rail.addEventListener('pointerdown', e => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = lastX = e.clientX; startL = rail.scrollLeft; lastT = performance.now(); v = 0;
        cancelAnimationFrame(raf);
      });
      addEventListener('pointermove', e => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 5 && !moved) { moved = true; rail.classList.add('is-drag'); }
        if (!moved) return;
        rail.scrollLeft = startL - dx;
        const now = performance.now(); v = (e.clientX - lastX) / Math.max(1, now - lastT); lastX = e.clientX; lastT = now;
      });
      addEventListener('pointerup', () => {
        if (!down) return; down = false;
        setTimeout(() => rail.classList.remove('is-drag'), 0);
        if (!moved || reduce) return;
        let vel = v * 16; // px per frame
        const glide = () => { if (Math.abs(vel) < 0.4) return; rail.scrollLeft -= vel; vel *= 0.94; raf = requestAnimationFrame(glide); };
        glide();
      });
      rail.addEventListener('click', e => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      rail.addEventListener('keydown', e => {
        if (e.target !== rail) return;
        if (e.key === 'ArrowRight') { rail.scrollBy({ left: step(), behavior: 'smooth' }); e.preventDefault(); }
        if (e.key === 'ArrowLeft') { rail.scrollBy({ left: -step(), behavior: 'smooth' }); e.preventDefault(); }
      });
    });
  }

  window.NTHMotion = {
    init(root = document) { initReveals(root); initRails(root); measure(); frame(); headerTick(); },
    refresh() { measure(); frame(); headerTick(); }
  };
  if (document.readyState !== 'loading') window.NTHMotion.init();
  else document.addEventListener('DOMContentLoaded', () => window.NTHMotion.init());
})();
